import { GEM_COSTS } from './constants.js';
import { isCursedUnlocked, DIFFICULTIES } from './gameStore.js';

export class UIController {
  constructor(store, soundController = null) {
    this.store = store;
    this.soundController = soundController;
    this.el = null;
    this.lastMana = -1;
    this.lastWave = -1;
    this.lastPhase = null;
    this.lastSpeed = -1;
    this.statsShown = false; // prevent flicker-updating stats every frame
    this.selectedDifficulty = 'polished';
  }

  init() {
    this.el = {
      manaFill:       document.getElementById('mana-fill'),
      manaValue:      document.getElementById('mana-value'),
      waveValue:      document.getElementById('wave-value'),
      waveDots:       document.querySelectorAll('.wave-dot'),
      speedToggle:    document.getElementById('speed-toggle'),
      titleScreen:    document.getElementById('title-screen'),
      gameOverScreen: document.getElementById('game-over-screen'),
      victoryScreen:  document.getElementById('victory-screen'),
      startBtn:       document.getElementById('start-btn'),
      goRestart:      document.getElementById('go-restart'),
      vicRestart:     document.getElementById('vic-restart'),
      goWaves:        document.getElementById('go-waves'),
      goMerges:       document.getElementById('go-merges'),
      goTier:         document.getElementById('go-tier'),
      goTime:         document.getElementById('go-time'),
      vicMerges:      document.getElementById('vic-merges'),
      vicTier:        document.getElementById('vic-tier'),
      vicTime:        document.getElementById('vic-time'),
      goCopySeed:     document.getElementById('go-copy-seed'),
      vicCopySeed:    document.getElementById('vic-copy-seed'),
      buyTier1:       document.getElementById('buy-tier1'),
      buyTier2:       document.getElementById('buy-tier2'),
      buyTier3:       document.getElementById('buy-tier3'),
    };

    this.el.startBtn.addEventListener('click',       () => this.handleStart());
    this.el.goRestart.addEventListener('click',      () => this.handleRestart());
    this.el.vicRestart.addEventListener('click',     () => this.handleRestart());
    this.el.speedToggle.addEventListener('click',    () => this.handleSpeedToggle());
    this.el.goCopySeed.addEventListener('click',     () => this.copySeedURL());
    this.el.vicCopySeed.addEventListener('click',    () => this.copySeedURL());
    this.el.buyTier1.addEventListener('click',       () => this.purchaseGem(1));
    this.el.buyTier2.addEventListener('click',       () => this.purchaseGem(2));
    this.el.buyTier3.addEventListener('click',       () => this.purchaseGem(3));

    // Initialize difficulty selector
    this.initDifficultySelector();
    
    this.startUpdateLoop();
  }
  
  initDifficultySelector() {
    const selector = document.getElementById('difficulty-selector');
    if (!selector) return;
    
    const options = selector.querySelectorAll('.difficulty-option');
    const cursedOption = document.getElementById('difficulty-cursed');
    
    // Check if Cursed is unlocked
    const cursedUnlocked = isCursedUnlocked();
    
    // If Cursed is unlocked, remove lock icon and locked styling
    if (cursedUnlocked && cursedOption) {
      cursedOption.classList.remove('locked');
      const lockIcon = cursedOption.querySelector('.diff-icon');
      if (lockIcon) {
        lockIcon.textContent = '💀';
      }
    }
    
    // Load saved difficulty from localStorage
    let savedDifficulty = 'polished';
    try {
      const stored = localStorage.getItem('gemgrid_difficulty');
      if (stored && DIFFICULTIES[stored]) {
        // Validate: can't load Cursed if not unlocked
        if (stored === 'cursed' && !cursedUnlocked) {
          savedDifficulty = 'polished';
        } else {
          savedDifficulty = stored;
        }
      }
    } catch (e) {
      // localStorage unavailable
    }
    
    // Set initial selection
    this.selectedDifficulty = savedDifficulty;
    options.forEach(o => {
      if (o.dataset.difficulty === savedDifficulty) {
        o.classList.add('selected');
      } else {
        o.classList.remove('selected');
      }
    });
    
    // Add click handlers to difficulty options
    options.forEach(option => {
      option.addEventListener('click', () => {
        const difficulty = option.dataset.difficulty;
        
        // Can't select Cursed if not unlocked
        if (difficulty === 'cursed' && !cursedUnlocked) {
          this.showToast('Beat Flawless to unlock Cursed!', 'error');
          return;
        }
        
        // Update selection
        options.forEach(o => o.classList.remove('selected'));
        option.classList.add('selected');
        this.selectedDifficulty = difficulty;
        
        // Save to localStorage
        try {
          localStorage.setItem('gemgrid_difficulty', difficulty);
        } catch (e) {
          // localStorage unavailable
        }
      });
    });
  }

  handleStart() {
    this.statsShown = false;
    this.store.getState().initGame(null, this.selectedDifficulty);
  }

  handleRestart() {
    this.statsShown = false;
    this.store.getState().initGame(null, this.selectedDifficulty);
  }

  handleSpeedToggle() {
    this.store.getState().toggleSpeed();
  }

  purchaseGem(tier) {
    const success = this.store.getState().purchaseGem(tier);
    if (success) {
      if (this.soundController) this.soundController.onPurchaseGem();
    } else {
      this.showToast('Not enough mana!', 'error');
    }
  }

  copySeedURL() {
    const state = this.store.getState();
    const seed = state.seed;
    if (!seed) return;

    const url = `${window.location.origin}${window.location.pathname}?seed=${seed}`;

    const doClipboard = navigator.clipboard && window.isSecureContext
      ? navigator.clipboard.writeText(url)
      : Promise.reject(new Error('clipboard unavailable'));

    doClipboard
      .then(() => this.showToast('Seed URL copied!', 'success'))
      .catch(() => this.showSeedModal(url));
  }

  showSeedModal(url) {
    const backdrop = document.createElement('div');
    backdrop.className = 'seed-modal-backdrop';
    backdrop.innerHTML = `
      <div class="seed-modal">
        <h3>Share this run</h3>
        <p>Clipboard access was denied. Copy the URL below:</p>
        <input type="text" value="${url}" readonly id="seed-modal-input" />
        <div class="btn-row">
          <button class="btn-secondary" id="seed-modal-close">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);

    const input = backdrop.querySelector('#seed-modal-input');
    input.select();
    input.setSelectionRange(0, 99999); // mobile

    backdrop.querySelector('#seed-modal-close').addEventListener('click', () => {
      document.body.removeChild(backdrop);
    });
    // Also close on backdrop click
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) document.body.removeChild(backdrop);
    });
  }

  showToast(message, type = '') {
    const toast = document.createElement('div');
    toast.className = `feedback-toast${type ? ' ' + type : ''}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 1100);
  }

  startUpdateLoop() {
    const tick = () => {
      this.update();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  update() {
    const state = this.store.getState();

    // ── Mana ───────────────────────────────────────────────
    if (state.mana !== this.lastMana) {
      const pct = (state.mana / state.maxMana) * 100;
      this.el.manaFill.style.width = `${pct}%`;
      this.el.manaValue.textContent = state.mana;

      // Low-mana warning colour
      if (state.mana <= 2) {
        this.el.manaFill.classList.add('low');
      } else {
        this.el.manaFill.classList.remove('low');
      }

      // Update purchase button states
      this.el.buyTier1.disabled = state.mana < GEM_COSTS[1] || state.phase !== 'playing';
      this.el.buyTier2.disabled = state.mana < GEM_COSTS[2] || state.phase !== 'playing';
      this.el.buyTier3.disabled = state.mana < GEM_COSTS[3] || state.phase !== 'playing';

      this.lastMana = state.mana;
    }

    // ── Wave counter & progress dots ───────────────────────
    if (state.currentWave !== this.lastWave) {
      this.el.waveValue.textContent = state.currentWave;
      this.el.waveDots.forEach((dot) => {
        const w = parseInt(dot.dataset.wave, 10);
        dot.classList.remove('done', 'current');
        if (w < state.currentWave) dot.classList.add('done');
        else if (w === state.currentWave && state.currentWave > 0) dot.classList.add('current');
      });
      this.lastWave = state.currentWave;
    }

    // ── Speed toggle ───────────────────────────────────────
    if (state.speedMultiplier !== this.lastSpeed) {
      const is2x = state.speedMultiplier === 2;
      this.el.speedToggle.textContent = is2x ? '2x Speed' : '1x Speed';
      this.el.speedToggle.classList.toggle('active', is2x);
      this.lastSpeed = state.speedMultiplier;
    }

    // ── Phase / screens ────────────────────────────────────
    if (state.phase !== this.lastPhase) {
      this.el.titleScreen.classList.toggle('hidden',    state.phase !== 'title');
      this.el.gameOverScreen.classList.toggle('hidden', state.phase !== 'gameover');
      this.el.victoryScreen.classList.toggle('hidden',  state.phase !== 'victory');

      if (state.phase === 'gameover' || state.phase === 'victory') {
        this.populateStats(state);
        this.statsShown = true;
      } else {
        this.statsShown = false;
      }

      // When returning to title, refresh difficulty selector (Cursed might be unlocked now)
      if (state.phase === 'title') {
        this.refreshDifficultySelector();
      }

      // Disable purchase buttons when not playing
      const playing = state.phase === 'playing';
      this.el.buyTier1.disabled = !playing || state.mana < GEM_COSTS[1];
      this.el.buyTier2.disabled = !playing || state.mana < GEM_COSTS[2];
      this.el.buyTier3.disabled = !playing || state.mana < GEM_COSTS[3];

      this.lastPhase = state.phase;
    }
  }
  
  // Refresh difficulty selector state (e.g., after winning to check if Cursed unlocked)
  refreshDifficultySelector() {
    const selector = document.getElementById('difficulty-selector');
    if (!selector) return;
    
    const options = selector.querySelectorAll('.difficulty-option');
    const cursedOption = document.getElementById('difficulty-cursed');
    
    // Check if Cursed is unlocked
    const cursedUnlocked = isCursedUnlocked();
    
    // If Cursed is now unlocked, remove lock icon and locked styling
    if (cursedUnlocked && cursedOption && cursedOption.classList.contains('locked')) {
      cursedOption.classList.remove('locked');
      const lockIcon = cursedOption.querySelector('.diff-icon');
      if (lockIcon) {
        lockIcon.textContent = '💀';
      }
    }
  }

  populateStats(state) {
    const elapsed = state.elapsedTime || ((Date.now() - state.startTime) / 1000);
    const tierLabel = state.highestTierReached > 0
      ? `Tier ${state.highestTierReached}`
      : '—';

    if (state.phase === 'gameover') {
      this.el.goWaves.textContent  = state.currentWave;
      this.el.goMerges.textContent = state.gemsMerged;
      this.el.goTier.textContent   = tierLabel;
      this.el.goTime.textContent   = this.formatTime(elapsed);
    } else {
      this.el.vicMerges.textContent = state.gemsMerged;
      this.el.vicTier.textContent   = tierLabel;
      this.el.vicTime.textContent   = this.formatTime(elapsed);
    }
  }

  formatTime(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
